/**
 * ReconciliationPage — Admin ops dashboard.
 * Toont inconsistente data + retry/fix knoppen.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Shield, AlertCircle, RefreshCw, FileText, Mail, CreditCard, Users, Wrench, ArrowRightCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ReconciliationPage() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api('get', '/api/admin/reconcile/all');
      setData(r.data);
    } catch (e) { toast.error('Reconcile mislukt'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const fixStuck = async () => {
    if (!window.confirm('Stop alle active sequences voor users die klant zijn?')) return;
    setActing(true);
    try {
      const r = await api('post', '/api/admin/reconcile/fix-stuck-sequences');
      toast.success(`${r.data.fixed} sequences gestopt`);
      load();
    } catch (e) { toast.error('Fix mislukt'); }
    setActing(false);
  };

  const backfillInvoices = async () => {
    if (!window.confirm('Genereer facturen voor alle paid payments zonder factuur?')) return;
    setActing(true);
    try {
      const r = await api('post', '/api/admin/invoices/backfill');
      toast.success(`${r.data.created_count} facturen aangemaakt`);
      load();
    } catch (e) { toast.error('Backfill mislukt'); }
    setActing(false);
  };

  const bulkEnroll = async () => {
    if (!window.confirm('Schrijf alle bestaande non-customer leads in voor de nurture sequence?')) return;
    setActing(true);
    try {
      const r = await api('post', '/api/admin/crm/bulk-enroll-legacy');
      toast.success(`${r.data.enrolled} leads ingeschreven (${r.data.skipped} overgeslagen)`);
      load();
    } catch (e) { toast.error('Enroll mislukt'); }
    setActing(false);
  };

  const sendDigest = async () => {
    setActing(true);
    try {
      const r = await api('post', '/api/admin/digest/send-now');
      toast.success(`Daily digest verstuurd naar ${r.data.sent} admin(s)`);
    } catch (e) { toast.error('Digest mislukt'); }
    setActing(false);
  };

  if (loading || !data) return <div className="text-center py-20 text-[#94a3b8]">Reconcile data laden...</div>;

  const sections = [
    { key: 'payments_zonder_invoice', label: 'Betalingen zonder factuur', icon: CreditCard, action: backfillInvoices, actionLabel: 'Backfill facturen', color: '#dc2626' },
    { key: 'invoices_zonder_paid_payment', label: 'Facturen zonder paid payment', icon: FileText, color: '#f97316' },
    { key: 'active_sequence_voor_klant', label: 'Active sequences voor klanten', icon: Users, action: fixStuck, actionLabel: 'Stop sequences', color: '#D4A017' },
    { key: 'launch_maand_stuck', label: 'Launch maand overdue (>30d)', icon: AlertCircle, color: '#dc2626' },
    { key: 'failed_emails', label: 'Definitief gefaalde emails', icon: Mail, color: '#dc2626' },
    { key: 'failed_payments', label: 'Failed payments (30d)', icon: CreditCard, color: '#dc2626' },
  ];

  const total = data.total_issues || 0;

  return (
    <div data-testid="reconciliation-page">
      <div className="flex justify-between items-start flex-wrap gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0f172a] flex items-center gap-2">
            <Shield size={20} className="text-[#D4A017]" /> Reconciliation Dashboard
          </h1>
          <p className="text-sm text-[#64748b] mt-1">{total === 0 ? '✅ Alle systemen consistent' : `⚠ ${total} items vereisen aandacht`}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={load} className="text-xs bg-white border border-slate-200 hover:border-[#D4A017] text-[#0f172a] font-bold px-3 py-2 rounded-lg flex items-center gap-1.5">
            <RefreshCw size={12} /> Refresh
          </button>
          <button onClick={bulkEnroll} disabled={acting} data-testid="bulk-enroll-btn" className="text-xs bg-[#D4A017] hover:bg-[#b88a13] text-[#0f172a] font-bold px-3 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-50">
            <ArrowRightCircle size={12} /> Bulk-enroll legacy leads
          </button>
          <button onClick={sendDigest} disabled={acting} data-testid="send-digest-btn" className="text-xs bg-[#0f172a] hover:bg-[#1e1b4b] text-white font-bold px-3 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-50">
            <Mail size={12} /> Stuur daily digest nu
          </button>
        </div>
      </div>

      {total === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-xl p-10 text-center">
          <Shield size={32} className="text-green-700 mx-auto mb-3" />
          <p className="font-bold text-green-900 text-lg">Geen issues gevonden</p>
          <p className="text-sm text-green-700 mt-1">Alle betalingen hebben facturen, geen stuck sequences, geen failed jobs.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {sections.map(s => {
            const items = data[s.key] || [];
            if (items.length === 0) return null;
            const Icon = s.icon;
            return (
              <div key={s.key} data-testid={`section-${s.key}`} className="bg-white border border-slate-200 rounded-xl">
                <div className="p-3 border-b border-slate-200 flex justify-between items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded flex items-center justify-center text-white" style={{ background: s.color }}><Icon size={14} /></div>
                    <p className="font-bold text-sm text-[#0f172a]">{s.label}</p>
                    <span className="bg-slate-100 text-[#64748b] text-[10px] font-bold px-2 py-0.5 rounded">{items.length}</span>
                  </div>
                  {s.action && (
                    <button data-testid={`fix-${s.key}`} onClick={s.action} disabled={acting} className="bg-[#D4A017] hover:bg-[#b88a13] text-[#0f172a] text-[10px] font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50">
                      <Wrench size={10} /> {s.actionLabel}
                    </button>
                  )}
                </div>
                <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
                  {items.slice(0, 20).map((it, i) => (
                    <div key={i} className="p-3 text-xs">
                      <p className="font-bold text-[#0f172a] truncate">
                        {it.invoice_number || it.name || it.company_name || it.email || it.id || it.to}
                      </p>
                      <p className="text-[#64748b] mt-0.5 truncate">
                        {it.email && <span>{it.email}</span>}
                        {it.amount && <span> · €{it.amount}</span>}
                        {it.launch_started_at && <span> · started: {it.launch_started_at.slice(0, 10)}</span>}
                        {it.created_at && <span> · {it.created_at.slice(0, 16).replace('T', ' ')}</span>}
                        {it.last_error && <span className="text-red-600"> · {it.last_error.slice(0, 80)}</span>}
                      </p>
                    </div>
                  ))}
                  {items.length > 20 && <p className="p-2 text-center text-[10px] text-[#94a3b8]">+{items.length - 20} meer...</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
